import React, { useState, useEffect } from 'react';
import { apiRequest } from '@/utils/api-request';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Map, Plus, X } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface AreaData {
    areaId: number;
    areaName: string;
    city_id_fk: number;
    cityName: string;
    lat: string | null;
    lng: string | null;
    schools_count: number;
}

interface CityData {
    cityId: number;
    cityName: string;
    country_id?: number;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

interface AreaFormData {
    areaId?: number;
    areaName: string;
    city_id_fk: number | '';
    lat: string;
    lng: string;
}

const AreasAdmin: React.FC = () => {
    const [areas, setAreas] = useState<AreaData[]>([]);
    const [allCities, setAllCities] = useState<CityData[]>([]);
    const [cities, setCities] = useState<CityData[]>([]);
    const [cityCountryFilter, setCityCountryFilter] = useState<number>(1);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25,
    });
    const [sortColumn, setSortColumn] = useState('schools_count');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState<AreaFormData>({
        areaName: '',
        city_id_fk: '',
        lat: '',
        lng: '',
    });

    // Fetch cities for dropdown
    const fetchCities = async () => {
        try {
            const response = await apiRequest('/api/cities');
            if (response.success) {
                const all = response.data.cities || [];
                setAllCities(all);
                setCities(all.filter((c: CityData) => (c.country_id ?? 1) === cityCountryFilter));
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
        }
    };

    // Fetch areas
    const fetchAreas = async (page: number = pagination.currentPage) => {
        try {
            setIsLoading(true);
            const response = await apiRequest('/api/areas', {
                method: 'POST',
                body: JSON.stringify({
                    page,
                    limit: pagination.itemsPerPage,
                    search_term: searchTerm,
                    sort_column: sortColumn,
                    sort_order: sortDirection,
                }),
            });

            if (response.success) {
                setAreas(response.data.areas || []);
                setPagination(response.data.pagination);
            } else {
                showToast('Failed to fetch areas', 'error');
            }
        } catch (error) {
            console.error('Error fetching areas:', error);
            showToast('Failed to fetch areas', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Load cities on mount
    useEffect(() => {
        fetchCities();
    }, []);

    // Re-filter cities when country filter changes in modal
    useEffect(() => {
        setCities(allCities.filter((c: CityData) => (c.country_id ?? 1) === cityCountryFilter));
        setFormData(prev => ({ ...prev, city_id_fk: '' }));
    }, [cityCountryFilter, allCities]);

    // Load areas on mount and when filters change
    useEffect(() => {
        fetchAreas(1);
    }, [searchTerm, sortColumn, sortDirection]);

    // Open modal for adding new area
    const handleAddNew = () => {
        setFormData({
            areaName: '',
            city_id_fk: '',
            lat: '',
            lng: '',
        });
        setIsEditing(false);
        setIsModalOpen(true);
    };

    // Open modal for editing area
    const handleEdit = (area: AreaData) => {
        setFormData({
            areaId: area.areaId,
            areaName: area.areaName,
            city_id_fk: area.city_id_fk,
            lat: area.lat || '',
            lng: area.lng || '',
        });
        setIsEditing(true);
        setIsModalOpen(true);
    };

    // Close modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFormData({
            areaName: '',
            city_id_fk: '',
            lat: '',
            lng: '',
        });
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.areaName.trim()) {
            showToast('Area name is required', 'error');
            return;
        }

        if (!formData.city_id_fk) {
            showToast('Please select a city', 'error');
            return;
        }

        if (!formData.lat.trim()) {
            showToast('Latitude is required', 'error');
            return;
        }

        if (isNaN(Number(formData.lat))) {
            showToast('Latitude must be a valid number', 'error');
            return;
        }

        if (!formData.lng.trim()) {
            showToast('Longitude is required', 'error');
            return;
        }

        if (isNaN(Number(formData.lng))) {
            showToast('Longitude must be a valid number', 'error');
            return;
        }

        try {
            setIsSaving(true);

            const payload = {
                areaName: formData.areaName.trim(),
                city_id_fk: formData.city_id_fk,
                lat: formData.lat.trim(),
                lng: formData.lng.trim(),
            };

            let response;
            if (isEditing && formData.areaId) {
                response = await apiRequest(`/api/areas/${formData.areaId}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload),
                });
            } else {
                response = await apiRequest('/api/areas/create', {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });
            }

            if (response.success) {
                showToast(isEditing ? 'Area updated successfully' : 'Area created successfully', 'success');
                handleCloseModal();
                fetchAreas(pagination.currentPage);
            } else {
                showToast(response.message || 'Failed to save area', 'error');
            }
        } catch (error) {
            console.error('Error saving area:', error);
            showToast('Failed to save area', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchAreas(newPage);
        }
    };

    // Handle sort
    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center gap-4">
                    {/* Left Side - Icon, Title with Badge */}
                    <div className="flex items-center gap-3 flex-1">
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <Map className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Areas Management</h1>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                    {pagination.totalItems.toLocaleString()}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">Manage areas and localities</p>
                        </div>
                    </div>

                    {/* Right Side - Search and Add Button */}
                    <div className="flex items-center gap-3 flex-1">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search areas..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <Button
                            variant="primary"
                            onClick={handleAddNew}
                            leftIcon={<Plus size={18} />}
                        >
                            Add Area
                        </Button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-4 mt-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-brandColor text-white sticky top-0 z-10">
                            <tr>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('areaId')}
                                >
                                    ID {sortColumn === 'areaId' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('areaName')}
                                >
                                    Area Name {sortColumn === 'areaName' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('cityName')}
                                >
                                    City {sortColumn === 'cityName' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('lat')}
                                >
                                    Latitude {sortColumn === 'lat' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('lng')}
                                >
                                    Longitude {sortColumn === 'lng' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('schools_count')}
                                >
                                    Schools Count {sortColumn === 'schools_count' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                        Loading areas...
                                    </td>
                                </tr>
                            ) : areas.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                        No areas found
                                    </td>
                                </tr>
                            ) : (
                                areas.map((area, index) => (
                                    <tr
                                        key={area.areaId}
                                        className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                    >
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {area.areaId}
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            <button
                                                onClick={() => handleEdit(area)}
                                                className="text-brandColor hover:text-brand-hover font-semibold hover:underline text-left"
                                            >
                                                {area.areaName}
                                            </button>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {area.cityName || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {area.lat || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {area.lng || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                                {area.schools_count.toLocaleString()}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="text-sm text-gray-600">
                    Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                    {pagination.totalItems} results
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronsLeft size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronRight size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronsRight size={16} />
                    </Button>
                </div>
            </div>

            {/* Add/Edit Area Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                            onClick={handleCloseModal}
                        />

                        {/* Modal */}
                        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all">
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {isEditing ? 'Edit Area' : 'Add New Area'}
                                </h3>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Area Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.areaName}
                                        onChange={(e) => setFormData({ ...formData, areaName: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                        placeholder="Enter area name"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                    <div className="flex rounded-lg border border-gray-300 overflow-hidden mb-2">
                                        {[{ label: '🇮🇳 India', value: 1 }, { label: '🇦🇪 UAE', value: 2 }].map(opt => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => setCityCountryFilter(opt.value)}
                                                className={`flex-1 py-2 text-sm font-medium transition-colors ${cityCountryFilter === opt.value ? 'bg-brandColor text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        City *
                                    </label>
                                    <select
                                        value={formData.city_id_fk}
                                        onChange={(e) => setFormData({ ...formData, city_id_fk: Number(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                        required
                                    >
                                        <option value="">Select City</option>
                                        {cities.map((city) => (
                                            <option key={city.cityId} value={city.cityId}>
                                                {city.cityName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Latitude *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.lat}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^0-9.-]/g, '');
                                                setFormData({ ...formData, lat: value });
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                            placeholder="e.g., 17.385044"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Longitude *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.lng}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^0-9.-]/g, '');
                                                setFormData({ ...formData, lng: value });
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                            placeholder="e.g., 78.486671"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleCloseModal}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={isSaving}
                                        loading={isSaving}
                                    >
                                        {isEditing ? 'Update Area' : 'Create Area'}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AreasAdmin;
